import React, { useEffect, useMemo, useState } from "react";
import {
  getEmployeeMasters,
  getEmployeeMasterByEmail,
  verifyEmployeeProfile
} from "../../services/employeeService";
import { displayValue } from "../../utils/displayValue";

import "../../styles/hrPageShell.css";
const PROFILE_COMPLETION_STATUS = {
  SUBMITTED: "Submitted",
  RETURNED_FOR_CORRECTION: "Returned for Correction"
};

const PROFILE_VERIFICATION_STATUS = {
  NOT_VERIFIED: "Not Verified",
  PENDING_VERIFICATION: "Pending Verification",
  PENDING_HR_REVIEW: "Pending HR Review",
  VERIFIED: "Verified",
  REJECTED: "Rejected",
  CORRECTION_REQUIRED: "Correction Required"
};

function HRProfileReview() {
  const accounts = [];
  const reviewerEmail = accounts?.[0]?.username || "";

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [actionType, setActionType] = useState("");
  const [comments, setComments] = useState("");
  const [previewEmployee, setPreviewEmployee] = useState(null);

  const getValue = (record, ...keys) => {
    if (!record) return "";

    for (const key of keys) {
      const value = record[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }

    return "";
  };

  const getDisplay = (record, ...keys) => displayValue(getValue(record, ...keys));

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setMessage("");
      const rows = await getEmployeeMasters();
      setEmployees(rows || []);
    } catch (error) {
      console.error("Profile review load error:", error);
      setMessage(error.message || "Failed to load submitted profiles.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const getEmployeeName = (employee) => {
    const fullName = getDisplay(employee, "fullName", "FullName", "Title");
    if (fullName && fullName !== "-") return fullName;

    const firstLastName = [
      getDisplay(employee, "firstName", "FirstName"),
      getDisplay(employee, "lastName", "LastName")
    ].filter((value) => value && value !== "-").join(" ");

    return firstLastName || "";
  };

  const getEmployeeId = (employee) => {
    return getDisplay(employee, "employeeId", "EmployeeId", "EmpId") || "-";
  };

  const getEmployeePrimaryText = (employee) => {
    return getEmployeeName(employee) || getEmployeeId(employee);
  };

  const normalizeVerificationStatus = (employee) => {
    const status = getDisplay(employee, "profileVerificationStatus", "ProfileVerificationStatus");
    if (status === PROFILE_VERIFICATION_STATUS.PENDING_HR_REVIEW) return PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION;
    if (status === PROFILE_VERIFICATION_STATUS.CORRECTION_REQUIRED) return PROFILE_VERIFICATION_STATUS.REJECTED;
    return status || PROFILE_VERIFICATION_STATUS.NOT_VERIFIED;
  };

  const getEffectiveVerificationStatus = (employee) => {
    const completionStatus = getDisplay(employee, "profileCompletionStatus", "ProfileCompletionStatus");
    const verificationStatus = normalizeVerificationStatus(employee);

    // If employee resubmitted after correction, completion = Submitted must win over stale Rejected status.
    if (
      completionStatus === PROFILE_COMPLETION_STATUS.SUBMITTED &&
      verificationStatus === PROFILE_VERIFICATION_STATUS.REJECTED
    ) {
      return PROFILE_VERIFICATION_STATUS.NOT_VERIFIED;
    }

    return verificationStatus;
  };

  const canVerifyEmployee = (employee) => {
    const completionStatus = getDisplay(employee, "profileCompletionStatus", "ProfileCompletionStatus");
    const verificationStatus = getEffectiveVerificationStatus(employee);

    return (
      completionStatus === PROFILE_COMPLETION_STATUS.SUBMITTED &&
      [PROFILE_VERIFICATION_STATUS.NOT_VERIFIED, PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION].includes(verificationStatus)
    );
  };

  const submittedProfiles = useMemo(() => {
    return employees.filter((employee) => {
      const completionStatus = getDisplay(employee, "profileCompletionStatus", "ProfileCompletionStatus");
      const verificationStatus = normalizeVerificationStatus(employee);

      return (
        completionStatus === PROFILE_COMPLETION_STATUS.SUBMITTED ||
        completionStatus === PROFILE_COMPLETION_STATUS.RETURNED_FOR_CORRECTION ||
        verificationStatus === PROFILE_VERIFICATION_STATUS.NOT_VERIFIED ||
        verificationStatus === PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION ||
        verificationStatus === PROFILE_VERIFICATION_STATUS.VERIFIED ||
        verificationStatus === PROFILE_VERIFICATION_STATUS.REJECTED
      );
    });
  }, [employees]);

  const filteredProfiles = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return submittedProfiles.filter((employee) => {
      const verificationStatus = getEffectiveVerificationStatus(employee);
      const matchesStatus = !statusFilter || verificationStatus === statusFilter;

      const matchesSearch =
        !query ||
        getDisplay(employee, "employeeId", "EmployeeId", "EmpId").toLowerCase().includes(query) ||
        getDisplay(employee, "workEmail", "WorkEmail").toLowerCase().includes(query) ||
        getEmployeeName(employee).toLowerCase().includes(query) ||
        getDisplay(employee, "department", "Department").toLowerCase().includes(query) ||
        getDisplay(employee, "designation", "Designation").toLowerCase().includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [submittedProfiles, searchText, statusFilter]);

  const loadFullProfileForPreview = async (employee) => {
    try {
      setLoadingPreview(true);
      setMessage("");

      const workEmail = getDisplay(employee, "workEmail", "WorkEmail");
      if (!workEmail) {
        setPreviewEmployee(employee);
        return;
      }

      const result = await getEmployeeMasterByEmail(workEmail);
      setPreviewEmployee({ ...employee, ...(result.employee || {}) });
    } catch (error) {
      console.error("Profile preview load error:", error);
      setMessage("Full profile could not be loaded. Showing available list data only.");
      setMessageType("error");
      setPreviewEmployee(employee);
    } finally {
      setLoadingPreview(false);
    }
  };

  const openActionModal = (employee, action) => {
    setSelectedEmployee(employee);
    setActionType(action);
    setComments(action === "approve" ? "Profile verified." : "");
    setMessage("");
  };

  const closeActionModal = () => {
    setSelectedEmployee(null);
    setActionType("");
    setComments("");
  };

  const handleVerifyAction = async () => {
    if (!selectedEmployee || !actionType) return;

    if (actionType === "reject" && !comments.trim()) {
      alert("Please enter correction comments before returning the profile.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      await verifyEmployeeProfile({
        employeeId: getDisplay(selectedEmployee, "employeeId", "EmployeeId", "EmpId"),
        workEmail: getDisplay(selectedEmployee, "workEmail", "WorkEmail"),
        action: actionType,
        comments,
        verifiedBy: reviewerEmail
      });

      setMessage(actionType === "approve" ? "Profile approved successfully." : "Profile returned for correction successfully.");
      setMessageType("success");
      closeActionModal();
      await loadEmployees();
    } catch (error) {
      console.error("Profile verification error:", error);
      setMessage(error.message || "Verification action failed.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const getBadgeStyle = (status) => {
    if (status === PROFILE_VERIFICATION_STATUS.VERIFIED) return { ...badgeStyle, color: "#166534", backgroundColor: "#dcfce7", borderColor: "#bbf7d0" };
    if (status === PROFILE_VERIFICATION_STATUS.REJECTED) return { ...badgeStyle, color: "#991b1b", backgroundColor: "#fee2e2", borderColor: "#fecaca" };
    if (status === PROFILE_VERIFICATION_STATUS.PENDING_VERIFICATION) return { ...badgeStyle, color: "#92400e", backgroundColor: "#fef3c7", borderColor: "#fde68a" };
    return { ...badgeStyle, color: "var(--fm-page-accent)", backgroundColor: "#e0f2fe", borderColor: "#bae6fd" };
  };

  const pendingCount = submittedProfiles.filter((employee) => canVerifyEmployee(employee)).length;
  const verifiedCount = submittedProfiles.filter((employee) => getEffectiveVerificationStatus(employee) === PROFILE_VERIFICATION_STATUS.VERIFIED).length;
  const rejectedCount = submittedProfiles.filter((employee) => getEffectiveVerificationStatus(employee) === PROFILE_VERIFICATION_STATUS.REJECTED).length;

  const renderField = (label, ...keys) => (
    <div style={previewFieldStyle}>
      <div style={previewLabelStyle}>{label}</div>
      <div style={previewValueStyle}>{getDisplay(previewEmployee, ...keys) || "-"}</div>
    </div>
  );

  const renderLinkField = (label, ...keys) => {
    const value = getDisplay(previewEmployee, ...keys);
    return (
      <div style={previewFieldStyle}>
        <div style={previewLabelStyle}>{label}</div>
        {value ? (
          <a href={value} target="_blank" rel="noreferrer" style={linkStyle}>Open</a>
        ) : (
          <div style={previewValueStyle}>-</div>
        )}
      </div>
    );
  };

  return (
    <div className="fm-hr-page-shell" style={pageStyle}>
      <div className="fm-hr-hero" style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Profile Review</h1>
          <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.88)" }}>Review employee profiles submitted for HR verification.</p>
        </div>
        <button type="button" style={refreshButtonStyle} onClick={loadEmployees} disabled={loading}>{loading ? "Loading..." : "Refresh"}</button>
      </div>

      {message && <div style={messageType === "error" ? errorMessageStyle : successMessageStyle}>{message}</div>}

      <div style={kpiGridStyle}>
        <div style={kpiCardStyle}><div style={kpiLabelStyle}>Pending Review</div><div style={kpiValueStyle}>{pendingCount}</div></div>
        <div style={kpiCardStyle}><div style={kpiLabelStyle}>Verified</div><div style={kpiValueStyle}>{verifiedCount}</div></div>
        <div style={kpiCardStyle}><div style={kpiLabelStyle}>Returned / Rejected</div><div style={kpiValueStyle}>{rejectedCount}</div></div>
      </div>

      <div style={cardStyle}>
        <div style={filterGridStyle}>
          <input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search name, employee ID, email, department..." style={inputStyle} />
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={inputStyle}>
            <option value="">All Verification Statuses</option>
            <option value="Not Verified">Not Verified</option>
            <option value="Pending Verification">Pending Verification</option>
            <option value="Verified">Verified</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Work Email</th>
                <th style={thStyle}>Department</th>
                <th style={thStyle}>Designation</th>
                <th style={thStyle}>Completion</th>
                <th style={thStyle}>Verification</th>
                <th style={thStyle}>Stage</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProfiles.length === 0 && (
                <tr><td style={emptyCellStyle} colSpan="8">{loading ? "Loading submitted profiles..." : "No profiles found for selected filter."}</td></tr>
              )}

              {filteredProfiles.map((employee) => {
                const verificationStatus = getEffectiveVerificationStatus(employee);
                const isVerifiable = canVerifyEmployee(employee);

                return (
                  <tr key={getDisplay(employee, "id", "ID") || getEmployeeId(employee)}>
                    <td style={tdStyle}>
                      <strong>{getEmployeePrimaryText(employee)}</strong>
                      {getEmployeeName(employee) && getEmployeeId(employee) !== getEmployeeName(employee) && (
                        <div style={smallTextStyle}>{getEmployeeId(employee)}</div>
                      )}
                    </td>
                    <td style={tdStyle}>{getDisplay(employee, "workEmail", "WorkEmail")}</td>
                    <td style={tdStyle}>{getDisplay(employee, "department", "Department")}</td>
                    <td style={tdStyle}>{getDisplay(employee, "designation", "Designation")}</td>
                    <td style={tdStyle}>{getDisplay(employee, "profileCompletionStatus", "ProfileCompletionStatus")}</td>
                    <td style={tdStyle}><span style={getBadgeStyle(verificationStatus)}>{verificationStatus}</span></td>
                    <td style={tdStyle}>{getDisplay(employee, "onboardingStage", "OnboardingStage")}</td>
                    <td style={tdStyle}>
                      <div style={actionButtonGroupStyle}>
                        <button type="button" style={viewButtonStyle} disabled={loadingPreview} onClick={() => loadFullProfileForPreview(employee)}>View Profile</button>
                        <button type="button" style={approveButtonStyle} disabled={!isVerifiable || saving} onClick={() => openActionModal(employee, "approve")}>Approve</button>
                        <button type="button" style={rejectButtonStyle} disabled={!isVerifiable || saving} onClick={() => openActionModal(employee, "reject")}>Return</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {previewEmployee && (
        <div style={modalOverlayStyle}>
          <div style={largeModalBoxStyle}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={{ margin: 0 }}>Employee Profile Details</h2>
                <p style={{ marginTop: "6px", color: "rgba(255,255,255,0.88)" }}>{getEmployeePrimaryText(previewEmployee)}</p>
              </div>
              <button type="button" style={secondaryButtonStyle} onClick={() => setPreviewEmployee(null)}>Close</button>
            </div>

            <h3 style={previewSectionTitleStyle}>Organization</h3>
            <div style={previewGridStyle}>
              {renderField("Employee ID", "employeeId", "EmployeeId", "EmpId")}
              {renderField("Work Email", "workEmail", "WorkEmail")}
              {renderField("Department", "department", "Department")}
              {renderField("Designation", "designation", "Designation")}
              {renderField("Grade Level", "gradeLevel", "GradeLevel")}
              {renderField("Reporting Manager", "reportingManager", "ReportingManager", "reportManger")}
              {renderField("Location", "location", "Location")}
              {renderField("Joining Date", "joiningDate", "JoiningDate")}
            </div>

            <h3 style={previewSectionTitleStyle}>Personal & Address</h3>
            <div style={previewGridStyle}>
              {renderField("First Name", "firstName", "FirstName")}
              {renderField("Last Name", "lastName", "LastName")}
              {renderField("Full Name", "fullName", "FullName", "Title")}
              {renderField("Mobile", "mobile", "Mobile")}
              {renderField("Personal Email", "personalEmail", "PersonalEmail")}
              {renderField("Date of Birth", "dob", "DOB")}
              {renderField("Gender", "gender", "Gender")}
              {renderField("Nationality", "nationality", "Nationality")}
              {renderField("Address 1", "address1", "Address1")}
              {renderField("Address 2", "address2", "Address2")}
              {renderField("City", "city", "City")}
              {renderField("State", "state", "State")}
              {renderField("Postal Code", "postalCode", "PostalCode")}
              {renderField("Country", "country", "Country")}
            </div>

            <h3 style={previewSectionTitleStyle}>Emergency & Medical</h3>
            <div style={previewGridStyle}>
              {renderField("Emergency Contact", "emergencyContactName", "EmergencyContactName")}
              {renderField("Emergency Phone", "emergencyContactPhone", "EmergencyContactPhone")}
              {renderField("Relationship", "relationship", "Relationship")}
              {renderField("Blood Group", "bloodGroup", "BloodGroup")}
              {renderField("Family Doctor", "familyDoctorDetails", "FamilyDoctorDetails")}
              {renderField("Medical History", "medicalHistory", "MedicalHistory")}
            </div>

            <h3 style={previewSectionTitleStyle}>Qualification & Experience</h3>
            <div style={previewGridStyle}>
              {renderField("Qualification", "qualification", "Qualification")}
              {renderField("Institute", "institute", "Institute")}
              {renderField("Graduation Year", "graduationYear", "GraduationYear")}
              {renderField("Experience", "experience", "Experience")}
              {renderField("Domain", "domain", "Domain")}
              {renderField("Skills", "skills", "Skills")}
              {renderField("Last Company", "lastCompany", "LastCompany")}
              {renderField("Last Designation", "lastDesignation", "LastDesignation")}
              {renderField("Qualification Summary", "qualificationSummary", "QualificationSummary")}
              {renderField("Experience Summary", "experienceSummary", "ExperienceSummary")}
              {renderField("Achievements", "achievements", "Achievements")}
              {renderField("Certifications", "certifications", "Certifications")}
            </div>

            <h3 style={previewSectionTitleStyle}>Documents & Links</h3>
            <div style={previewGridStyle}>
              {renderLinkField("Profile Photo", "profilePhotoLink", "ProfilePhotoLink")}
              {renderLinkField("Resume", "resumeLink", "ResumeLink")}
              {renderLinkField("Attachments", "attachmentsLink", "AttachmentsLink")}
              {renderLinkField("LinkedIn", "linkedin", "LinkedIn")}
              {renderLinkField("GitHub", "github", "GitHub")}
              {renderLinkField("Portfolio", "portfolio", "Portfolio")}
            </div>
          </div>
        </div>
      )}

      {selectedEmployee && (
        <div style={modalOverlayStyle}>
          <div style={modalBoxStyle}>
            <h2 style={{ marginTop: 0 }}>{actionType === "approve" ? "Approve Profile" : "Return Profile for Correction"}</h2>
            <p style={{ color: "var(--fm-text)" }}><strong>{getEmployeePrimaryText(selectedEmployee)}</strong></p>
            <label style={labelStyle}>{actionType === "approve" ? "Verification Comments" : "Correction Comments *"}</label>
            <textarea value={comments} onChange={(event) => setComments(event.target.value)} rows={5} style={textareaStyle} placeholder={actionType === "approve" ? "Example: Profile verified." : "Example: Please correct emergency contact and upload latest resume."} />
            <div style={modalActionsStyle}>
              <button type="button" style={secondaryButtonStyle} onClick={closeActionModal} disabled={saving}>Cancel</button>
              <button type="button" style={actionType === "approve" ? approveButtonStyle : rejectButtonStyle} onClick={handleVerifyAction} disabled={saving}>{saving ? "Saving..." : actionType === "approve" ? "Approve" : "Return for Correction"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", backgroundColor: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" };
const headerStyle = { display: "flex", justifyContent: "space-between", gap: "18px", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "16px", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" };
const cardStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", minWidth: 0, overflow: "hidden" };
const kpiGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "11px", marginBottom: "14px", minWidth: 0 };
const kpiCardStyle = { ...cardStyle, padding: "13px" };
const kpiLabelStyle = { color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 750 };
const kpiValueStyle = { color: "var(--fm-page-accent)", fontSize: "26px", fontWeight: 800, marginTop: "4px", lineHeight: 1.15 };
const filterGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "14px", minWidth: 0 };
const inputStyle = { height: "40px", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "8px 10px", fontSize: "13px", backgroundColor: "var(--fm-card)", width: "100%", minWidth: 0, boxSizing: "border-box" };
const textareaStyle = { width: "100%", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px", fontSize: "13px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" };
const tableWrapperStyle = { overflowX: "auto", maxWidth: "100%", border: "1px solid var(--fm-border)", borderRadius: "14px", WebkitOverflowScrolling: "touch" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "12.5px", minWidth: "980px" };
const thStyle = { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", backgroundColor: "#f8fafc", color: "var(--fm-text)", whiteSpace: "nowrap", fontWeight: 800 };
const tdStyle = { borderBottom: "1px solid var(--fm-border)", padding: "10px", color: "var(--fm-text)", verticalAlign: "top", whiteSpace: "nowrap" };
const emptyCellStyle = { padding: "18px", textAlign: "center", color: "var(--fm-muted)" };
const smallTextStyle = { color: "var(--fm-muted)", fontSize: "12px", marginTop: "3px" };
const badgeStyle = { display: "inline-flex", border: "1px solid", borderRadius: "999px", padding: "5px 10px", fontSize: "11.5px", fontWeight: 750, whiteSpace: "nowrap" };
const actionButtonGroupStyle = { display: "flex", gap: "8px", flexWrap: "wrap" };
const viewButtonStyle = { backgroundColor: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "10px", padding: "8px 12px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const approveButtonStyle = { backgroundColor: "#047857", color: "#ffffff", border: "1px solid #047857", borderRadius: "10px", padding: "8px 12px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const rejectButtonStyle = { backgroundColor: "#b91c1c", color: "#ffffff", border: "1px solid #b91c1c", borderRadius: "10px", padding: "8px 12px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const refreshButtonStyle = { backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const secondaryButtonStyle = { backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const modalOverlayStyle = { position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px", zIndex: 1000 };
const modalBoxStyle = { width: "100%", maxWidth: "560px", backgroundColor: "var(--fm-card)", borderRadius: "18px", padding: "22px", boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)" };
const largeModalBoxStyle = { width: "100%", maxWidth: "1000px", maxHeight: "88vh", overflowY: "auto", backgroundColor: "var(--fm-card)", borderRadius: "18px", padding: "22px", boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)" };
const modalHeaderStyle = { display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", borderBottom: "1px solid var(--fm-border)", paddingBottom: "12px", marginBottom: "14px", flexWrap: "wrap" };
const modalActionsStyle = { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px", flexWrap: "wrap" };
const labelStyle = { display: "block", marginBottom: "6px", fontWeight: 750, color: "var(--fm-text)" };
const successMessageStyle = { border: "1px solid #bbf7d0", backgroundColor: "#ecfdf5", color: "#047857", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const errorMessageStyle = { border: "1px solid #fecaca", backgroundColor: "#fef2f2", color: "#b91c1c", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const previewGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "12px", marginBottom: "16px" };
const previewFieldStyle = { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px", backgroundColor: "#f8fafc" };
const previewLabelStyle = { color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 750, marginBottom: "4px" };
const previewValueStyle = { color: "var(--fm-text)", fontSize: "13px", wordBreak: "break-word" };
const previewSectionTitleStyle = { marginTop: "18px", marginBottom: "10px", color: "var(--fm-text)" };
const linkStyle = { color: "var(--fm-page-accent)", fontWeight: 750, textDecoration: "none" };
export default HRProfileReview;
